<template>
  <ResizableColumns identifier="geo-fences-page" :first-column-min-size="27"  :first-column-default-size="27">
    <template #first-column>
      <div class="cols">
        <form class="cols" @submit.prevent="saveGeoFence">
          <label for="name">
            {{ $t('geo-fences.name') }}
          </label>
          <input
              id="name"
              class="stretch"
              v-model="name"
              @update:model-value="userPropertiesChanged()"
              maxlength="128"
              :disabled="!isCreateMode && !isEditMode"
              required
          />
          <label for="description">
            {{ $t('geo-fences.description') }}
          </label>
          <input
              id="description"
              class="stretch"
              v-model="description"
              @update:model-value="userPropertiesChanged()"
              maxlength="128"
              :disabled="!isCreateMode && !isEditMode"
              required
          />
          <div class="rows mb-10 radius-coordinates-container">
            <div class="third cols pr-10 radius-field">
              <label for="radius">
                {{ $t('geo-fences.radius') }}
              </label>
              <input
                  id="radius"
                  type="number"
                  min="1"
                  max="1500"
                  v-model="radius"
                  @update:model-value="userPropertiesChanged()"
                  :disabled="!isCreateMode && !isEditMode"
                  required
              />
            </div>
            <div class="cols width-100p">
              <label>{{ $t('geo-fences.coordinates') }}</label>
              <div class="rows mobile-cols width-100p">
                <input
                    id="latitude"
                    class="width-50p mr-5"
                    type="number"
                    step="any"
                    v-model="coordinates.lat"
                    @update:model-value="userPropertiesChanged()"
                    :disabled="!isCreateMode && !isEditMode"
                    required
                />
                <input
                    id="longitude"
                    class="width-50p"
                    type="number"
                    step="any"
                    v-model="coordinates.lng"
                    @update:model-value="userPropertiesChanged()"
                    :disabled="!isCreateMode && !isEditMode"
                    required
                />
              </div>
            </div>
          </div>
          <div class="rows mb-10">
            <div class="third cols pr-10">
                <label for="color">
                {{ $t('geofence.color') }}
              </label>
              <ChooseGeoFenceColor
                  id="color"
                  v-model="color"
                  :disabled="!isCreateMode && !isEditMode"
                  @update:model-value="userPropertiesChanged()"
              />
            </div>
          </div>
          <div>
            <button type="button" @click="newGeoFenceButtonClick" v-if="!this.isCreateMode && !isEditMode">
              {{ $t('geo-fences.add') }}
            </button>
            <button type="button" @click="cancelButtonClick" v-if="isCreateMode || isEditMode">
              {{ $t('generic.cancel') }}
            </button>
            <button type="submit" class="button-save" :disabled="!isCreateMode && !isEditMode">
              {{ $t('generic.save') }}
            </button>
          </div>
        </form>
        <DataGrid
            ref="geoFencesList"
            class="ml-0 mr-0 strech geo-fences-list fit-content fixed-header"
            :data-model="dataGridModel"
            :data="filteredUserGeoFences"
            :selected-id="selectedGeoFenceId"
            @on-select="geoFenceDataGridSelect"
            @on-delete="deleteGeoFence"
            allow-select
            allow-add
            allow-delete
        />
      </div>
    </template>
    <template #second-column>
      <div class="map-container">
        <Map ref="googleMapRef"
             class="map"
             mapTypeId="terrain"
             :center="center"
             :zoom="15"
             scrollwheel
        >
          <CircleGeoFence v-for="geoFenceData in userGeoFences" v-bind:key="geoFenceData.id"
                          ref="geoFenceAreasRef"
                          :geoFenceData="geoFenceData"
                          @click="geoFenceAreaClick"
                          @center_changed="geoFenceCenterChanged"
                          @radius_changed="geoFenceRadiusChanged" />
          <CircleGeoFence v-if="newGeoFenceData != null"
                          ref="newGeofenceAreaRef"
                          :geoFenceData="newGeoFenceData"
                          @click="geoFenceAreaClick"
                          @center_changed="geoFenceCenterChanged"
                          @radius_changed="geoFenceRadiusChanged" />
        </Map>
      </div>
    </template>
  </ResizableColumns>
</template>

<script>

import {mapState} from "vuex";
import {dataAggregator, GoogleMapsManager} from "@/assets/js/data-manipulation";
import DataGrid from "@/components/pages/partials/DataGrid.vue";
import Map from "@/components/pages/partials/maps/Map.vue"
import CircleGeoFence from "@/components/pages/partials/maps/CircleGeoFence.vue";
import {popups} from "@/assets/js/popups";
import {GeoFenceType} from "@/assets/js/enums/GeoFenceType.ts";
import {actions} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import ResizableColumns from "@/components/pages/partials/ResizableColumns.vue";
import ChooseGeoFenceColor from "@/components/pages/partials/ChooseGeoFenceColor.vue";
import {GeoFenceColor} from "@/assets/js/enums/GeoFenceColor.ts";

export default {
  components: {ChooseGeoFenceColor, CircleGeoFence, DataGrid, Map, ResizableColumns},
  name: 'GeoFencesPage',
  props: {
    searchToken: String,
  },
  data() {
    return {
      center: GoogleMapsManager.defaultCenter,

      selectedGeoFenceArea: null,
      newGeoFenceData: null,

      name: null,
      description: null,
      radius: null,
      coordinates: {lat: null, lng: null},
      color: null,
    }
  },
  computed: {
    ...mapState(['userGeoFences']),
    filteredUserGeoFences() {
      return dataAggregator.filterGeoFencesByToken(this.userGeoFences, this.searchToken)
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    dataGridModel() {
      return {
        headers: {
          [this.$t('geo-fences.name')]: 'name',
        },
      };
    },
    isSelectedGeoFenceEdited() {
      if (!this.hasSelectedGeoFence) {
        return false;
      }

      if (this.isCreateMode) {
        return true;
      }

      const initialData = this.selectedGeoFenceData;

      return this.radius !== initialData['area']['radius'] ||
          this.description !== initialData['description'] ||
          this.name !== initialData['name'] ||
          this.coordinates?.lat !== initialData['area']['coordinates']?.lat ||
          this.coordinates?.lng !== initialData['area']['coordinates']?.lng ||
          this.color !== initialData['attributes']['color'] ;
    },
    hasSelectedGeoFence() {
      return this.selectedGeoFenceArea !== null;
    },
    isCreateMode() {
      return this.selectedGeoFenceId === 0;
    },
    isEditMode() {
      return this.selectedGeoFenceId > 0;
    },
    selectedGeoFenceId() {
      return this.selectedGeoFenceArea?.id;
    },
    selectedGeoFenceData() {
      return this.selectedGeoFenceArea?.data
    }
  },
  methods: {
    newGeoFenceButtonClick() {
      this.changeGeoFenceFocus(() => {
        this.createNewGeoFence();
        setTimeout(() => {
          this.selectGeoFence(this.$refs.newGeofenceAreaRef);
          this.$refs.newGeofenceAreaRef?.showInfoWindow();
        });
      });
    },
    geoFenceDataGridSelect(id) {
      const area = this.getAreaById(id);
      if (this.selectedGeoFenceId === id) {
        area?.showInfoWindow();
        this.centerAreaOnMap(area);
        return;
      }

      this.changeGeoFenceFocus(() => {
        this.selectGeoFence(area);
        area?.showInfoWindow();
        this.centerAreaOnMap(area);
      });
    },
    async saveGeoFence() {
      if (!this.isCreateMode && !this.isEditMode) {
        return;
      }

      const geoFenceData = this.selectedGeoFenceData;
      geoFenceData.name = this.name;
      geoFenceData.description = this.description;
      geoFenceData.area = {
        coordinates: this.coordinates,
        radius: this.radius
      };
      geoFenceData.attributes = {
        color: this.color
      };

      const isCreateMode = this.isCreateMode;
      const action = isCreateMode ? actions.CREATE_USER_GEO_FENCE : actions.UPDATE_USER_GEO_FENCE;

      await this.$store.dispatch(action, geoFenceData)
          .then(result => {
            if (this.isCreateMode) {
              this.clearGeoFenceFocus();
            }
            this.$store.dispatch(actions.GET_USER_GEO_FENCES)
                .then(() => {
                  if (isCreateMode) {
                    const area = this.getAreaById(result.id);
                    if (area) {
                      this.selectGeoFence(area);
                      area.showInfoWindow();
                    }
                  }
                });
            notification.success(this.$t('generic.save.success'));
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    deleteGeoFence(id) {
      popups.yesNoConfirmation(this.$swal, this.$t('generic.delete.confirm'))
          .then(async result => {
            if (!result.isConfirmed) {
              return;
            }

            await this.$store.dispatch(actions.DELETE_USER_GEO_FENCE, {id})
                .then(() => {
                  this.clearGeoFenceFocus();
                  this.$store.dispatch(actions.GET_USER_GEO_FENCES);
                  notification.success(this.$t('generic.delete.success'));
                })
                .catch(e => notification.error(this.$t(e.message)));
          })
    },
    cancelButtonClick() {
      this.changeGeoFenceFocus();
    },
    geoFenceAreaClick(area) {
      if (this.selectedGeoFenceId === area.id) {
        area?.showInfoWindow();
        this.scrollDataGrid();
        return;
      }

      this.changeGeoFenceFocus(() => {
        this.selectGeoFence(area);
        area?.showInfoWindow();
        this.scrollDataGrid();
      });
    },
    geoFenceRadiusChanged(item) {
      this.radius = item.getRadius();
    },
    geoFenceCenterChanged(item) {
      this.coordinates = item.getCenter();
    },
    userPropertiesChanged() {
      if (!this.hasSelectedGeoFence) {
        return;
      }

      this.selectedGeoFenceArea.setRadius(this.radius);
      this.selectedGeoFenceArea.setInfoWindowName(this.name);
      this.selectedGeoFenceArea.setInfoWindowDescription(this.description);
      this.selectedGeoFenceArea.setColor(this.color);
      this.selectedGeoFenceArea.setCenter(this.coordinates);
    },
    selectGeoFence(area) {
      this.selectedGeoFenceArea = area;

      this.selectedGeoFenceArea.setEditable();
      this.name = this.selectedGeoFenceArea.data.name;
      this.description = this.selectedGeoFenceArea.data.description;
      this.radius = this.selectedGeoFenceArea.data.area.radius;
      this.coordinates = this.selectedGeoFenceArea.data.area.coordinates;
      this.color = this.selectedGeoFenceArea.data.attributes?.color;
    },
    createNewGeoFence() {
      this.newGeoFenceData = {
        type: GeoFenceType.Circle,
        id: 0,
        name: '',
        description: '',
        area: {
          radius: parseInt(process.env.VUE_APP_NEW_GEO_FENCE_CIRCLE_RADIUS),
          coordinates: GoogleMapsManager.getCoordinatesFromGoogleData(this.$refs.googleMapRef.map.center)
        },
        attributes: {
          color: GeoFenceColor.Green,
        },
      };
    },
    changeGeoFenceFocus(callback = null) {
      if (this.isSelectedGeoFenceEdited) {
        popups.yesNoConfirmation(this.$swal, this.$t('generic.edit.confirm'))
            .then(result => {
              if (!result.isConfirmed) {
                return;
              }

              this.selectedGeoFenceArea?.closeInfoWindow();
              this.selectedGeoFenceArea?.resetOptions();
              this.selectedGeoFenceArea?.setReadOnly();
              this.clearGeoFenceFocus();
              callback?.();
            })
      }
      else {
        this.selectedGeoFenceArea?.closeInfoWindow();
        this.selectedGeoFenceArea?.resetOptions();
        this.selectedGeoFenceArea?.setReadOnly();
        this.clearGeoFenceFocus();
        callback?.();
      }
    },
    clearGeoFenceFocus() {
      this.name = null;
      this.description = null;
      this.radius = null;
      this.coordinates = {lat: null, lng: null};
      this.selectedGeoFenceArea = null;
      this.newGeoFenceData = null;
      this.color = null;
    },
    centerAreaOnMap(area) {
      if (area) {
        this.center = area.getCenter();
      }
    },
    scrollDataGrid() {
      setTimeout(() => this.$refs.geoFencesList.scrollToSelectedRow());
    },
    getAreaById(id) {
      return this.$refs.geoFenceAreasRef.find(area => area.id === id)
    }
  }
}

</script>

<style lang="scss">

.geo-fences-list {
  max-height: calc(100vh - 380px) !important;
  height: calc(100vh - 380px) !important;

  table {
    tr {
      th, td {
        &:first-of-type {
          width: 80% !important;
        }
      }
    }
  }
}

.radius-coordinates-container {
  width: calc(100% - 15px);

  .radius-field {
    max-width: 100px;
  }
}

.button-save {
  float: right;
  margin-right: 20px;
}

.map-container {
  margin-top: 20px;
  margin-right: 20px;
  width: 100%;

  .map {
    width: 100%;
    height: calc(100vh - 120px);
    border-radius: 15px;
  }
}
</style>